import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bell, List, Megaphone, Moon, Sparkle, Sun, Pulse, SignOut, User } from '@phosphor-icons/react';

export default function AdminTopBar({
  setSidebarOpen,
  darkMode,
  setDarkMode,
  auditNewCount,
  addonPendingCount,
  hasUnreadNotifications,
  navigate,
  onMarkAllRead,
  initials,
  user,
  handleLogout,
}) {
  const totalCount = auditNewCount + addonPendingCount;

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 md:hidden text-slate-600 dark:text-slate-400">
          <List size={24} />
        </button>
        <div className="hidden sm:flex items-center gap-2">
          <Pulse size={16} className="text-emerald-500" />
          <span className="text-xs text-slate-500">System Online</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => setDarkMode(!darkMode)} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-400">
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* Notification bell */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="relative p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400">
              <Bell size={20} />
              {hasUnreadNotifications && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-900" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <h4 className="font-semibold text-sm">Notifications</h4>
              {totalCount > 0 && <Badge variant="secondary" className="text-[10px]">{totalCount}</Badge>}
            </div>
            {auditNewCount > 0 && (
              <DropdownMenuItem className="px-3 py-2.5 cursor-pointer" onClick={() => navigate('/admin/audit-leads')}>
                <Megaphone size={16} className="mr-2 text-red-500 flex-shrink-0" />
                <span className="text-sm">{auditNewCount} new audit lead{auditNewCount !== 1 ? 's' : ''}</span>
              </DropdownMenuItem>
            )}
            {addonPendingCount > 0 && (
              <DropdownMenuItem className="px-3 py-2.5 cursor-pointer" onClick={() => navigate('/admin/dealerships')}>
                <Sparkle size={16} className="mr-2 text-amber-500 flex-shrink-0" />
                <span className="text-sm">{addonPendingCount} pending add-on request{addonPendingCount !== 1 ? 's' : ''}</span>
              </DropdownMenuItem>
            )}
            {totalCount === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">No new notifications</div>}
            {totalCount > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="px-3 py-2 justify-center text-xs text-muted-foreground cursor-pointer" onClick={onMarkAllRead}>
                  Mark all read
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-tight">{user?.name || 'Admin'}</p>
                <p className="text-[10px] text-slate-500 leading-tight">Super Admin</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-3 py-2 border-b sm:hidden">
              <p className="text-sm font-medium">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground">Super Admin</p>
            </div>
            <DropdownMenuItem className="px-3 py-2 cursor-pointer" onClick={() => navigate('/admin/settings')}>
              <User size={16} className="mr-2 text-slate-500" />
              <span className="text-sm">Account</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="px-3 py-2 cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400" onClick={handleLogout}>
              <SignOut size={16} className="mr-2" />
              <span className="text-sm font-medium">Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
